import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/Candidate_dashboard.css"; // Import the CSS file
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faSignOutAlt, faUsers, faList, faFileExport, faDownload, faFileArchive } from "@fortawesome/free-solid-svg-icons";

function DepartmentSIP() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("unallotted"); // State to toggle between views
  const [downloadingId, setDownloadingId] = useState(null); // Track which file is being downloaded
  const [downloadingZip, setDownloadingZip] = useState(false); // Track zip download status
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [view]); // Refetch data when view changes

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      const endpoint =
        view === "unallotted"
          ? "/api/departments/unalloted_studentwise_data"
          : "/api/departments/alloted_studentwise_data";

      const studentsRes = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!studentsRes.ok) {
        const errorData = await studentsRes.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to fetch data");
      }

      const studentsData = await studentsRes.json();
      setStudents(studentsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(`Error: ${error.message}`);
      setLoading(false);
    }
  };

  // Function to download student documents
  const handleDownloadDocuments = async (sip_id, studentName) => {
    try {
      setDownloadingId(sip_id);
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      const response = await fetch(`/api/departments/student_documents/${sip_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 404) {
          alert("No documents found for this student.");
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to download documents");
        }
        return;
      }

      // Get the filename from Content-Disposition header or create one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${sip_id}_documents.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Download error:", error);
      alert(`Error downloading documents: ${error.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  // Function to download all student documents as zip
  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      const response = await fetch("/api/departments/download_zip", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 404) {
          alert("No documents found to download.");
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to download zip file");
        }
        return;
      }

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = "student_documents.zip";
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      alert("Zip file downloaded successfully!");
      
    } catch (error) {
      console.error("Zip download error:", error);
      alert(`Error downloading zip file: ${error.message}`);
    } finally {
      setDownloadingZip(false);
    }
  };

  const renderPreference = (preference) => {
    if (!preference) return "-";
    return `${preference.title || "No Title"} (${preference.id}) - ${
      preference.professor?.name || "No Professor"
    }`;
  };

  const renderAllottedProject = (student) => {
    if (!student.selected_project) return "Not allotted";
    return `${student.selected_project.title} (${student.selected_project.id})`;
  };

  const handleAllot = async (sip_id, project_id) => {
    if (!project_id) return;

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      const response = await fetch(`/api/departments/allotment/${sip_id}/${project_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to allot project");
      }

      const result = await response.json();
      alert(result.message);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Allot error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleUnallot = async (sip_id) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      const response = await fetch(`/api/departments/allotment/${sip_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to unallot project");
      }

      const result = await response.json();
      alert(result.message);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Unallot error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCSVExport = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Authorization token is missing.");
        alert("Please log in to access this page.");
        return;
      }

      // Determine the endpoint based on current view
      const exportEndpoint = view === "unallotted" 
        ? "/api/departments/department_data" 
        : "/api/departments/alloted_student_data";

      const response = await fetch(exportEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to export data");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      // Set appropriate filename based on view
      const filename = view === "unallotted" 
        ? "unallotted_students.csv" 
        : "allotted_students.csv";
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Export error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="cd-container">
      {/* Sidebar */}
      <aside className="cd-sidebar">
        <nav>
          <button
            className={`cd-btn ${view === "unallotted" ? "active" : ""}`}
            onClick={() => setView("unallotted")}
          >
            <FontAwesomeIcon icon={faUsers} />
            <span>Unallotted Students</span>
          </button>
          <button
            className={`cd-btn ${view === "allotted" ? "active" : ""}`}
            onClick={() => setView("allotted")}
          >
            <FontAwesomeIcon icon={faList} />
            <span>Allotted Students</span>
          </button>
          <button className="cd-btn" onClick={() => navigate("/login")}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="cd-main-content">
        {/* Export Buttons Container */}
        <div style={{ 
          display: "flex", 
          gap: "15px", 
          marginBottom: "20px",
          alignItems: "center"
        }}>
          {/* Export CSV Button */}
          <button 
            onClick={handleCSVExport}
            style={{
              backgroundColor: "#2196F3",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            <FontAwesomeIcon icon={faFileExport} />
            <span>Export as CSV</span>
          </button>

          {/* Download All Documents as ZIP Button */}
          <button 
            onClick={handleDownloadZip}
            disabled={downloadingZip}
            style={{
              backgroundColor: downloadingZip ? "#ccc" : "#ff9800",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              border: "none",
              cursor: downloadingZip ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            <FontAwesomeIcon icon={faFileArchive} />
            <span>{downloadingZip ? "Downloading ZIP..." : "Download  (ZIP)"}</span>
          </button>
        </div>

        <table className="cd-table">
          <thead>
            <tr>
              <th>SIP ID</th>
              <th>Name</th>
              <th>Preference 1</th>
              <th>Preference 2</th>
              <th>Preference 3</th>
              <th>Allotted Project</th>
              <th>Documents</th> {/* New column for documents */}
              {view === "unallotted" && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={view === "unallotted" ? 8 : 7} className="cd-table-empty">
                  No students found.
                </td>
              </tr>
            ) : (
              students.map((student) => (
                <tr key={student.sip_id}>
                  <td>{student.sip_id || "-"}</td>
                  <td>{student.name}</td>
                  <td>{renderPreference(student.pref1)}</td>
                  <td>{renderPreference(student.pref2)}</td>
                  <td>{renderPreference(student.pref3)}</td>
                  <td>{renderAllottedProject(student)}</td>
                  <td>
                    <button
                      onClick={() => handleDownloadDocuments(student.sip_id, student.name)}
                      disabled={downloadingId === student.sip_id}
                      style={{
                        backgroundColor: downloadingId === student.sip_id ? "#ccc" : "#4CAF50",
                        color: "white",
                        padding: "5px 10px",
                        borderRadius: "3px",
                        border: "none",
                        cursor: downloadingId === student.sip_id ? "wait" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        fontSize: "12px",
                        width: "100%",
                        justifyContent: "center"
                      }}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      <span>
                        {downloadingId === student.sip_id ? "Downloading..." : "Download Docs"}
                      </span>
                    </button>
                  </td>
                  {view === "unallotted" && (
                    <td>
                      <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                        {/* Allotment dropdown */}
                        <select
                          onChange={(e) => handleAllot(student.sip_id, e.target.value)}
                          defaultValue=""
                          style={{ width: "100%", padding: "5px" }}
                        >
                          <option value="" disabled>
                            Select Project to Allot
                          </option>
                          {student.pref1 && (
                            <option value={student.pref1.id}>
                              {student.pref1.title || "No Title"} (Pref 1)
                            </option>
                          )}
                          {student.pref2 && (
                            <option value={student.pref2.id}>
                              {student.pref2.title || "No Title"} (Pref 2)
                            </option>
                          )}
                          {student.pref3 && (
                            <option value={student.pref3.id}>
                              {student.pref3.title || "No Title"} (Pref 3)
                            </option>
                          )}
                        </select>
                        
                        {/* Unallot button - Only show if student has an allotted project */}
                        {student.selected_project && (
                          <button
                            onClick={() => handleUnallot(student.sip_id)}
                            style={{
                              backgroundColor: "#f44336",
                              color: "white",
                              padding: "5px 10px",
                              borderRadius: "3px",
                              border: "none",
                              cursor: "pointer",
                              width: "100%"
                            }}
                          >
                            Unallot Current Project
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DepartmentSIP;